import { NestedHome, NestedProject } from '@habity/types';

export interface EmbeddedLink {
  url: string;
  title: string;
  category: string;
  embedUrl: string;
  platform: EmbedPlatform;
}

type ProviderData = {
  advertiseOn?: {
    links?: Array<{
      links?: string[];
      text?: string;
      category?: string;
    }>;
  };
};

/**
 * Extracts embeddable links from provider data (works for both homes and projects)
 */
export const extractEmbeddedLinks = (
  entity: NestedHome | NestedProject
): EmbeddedLink[] => {
  let providerData = entity?.provider_data as ProviderData;

  if (!providerData) {
    return [];
  }

  if (typeof providerData === 'string') {
    try {
      providerData = JSON.parse(providerData);
    } catch {
      return [];
    }
  }

  const advertiseOn = providerData.advertiseOn;

  if (!advertiseOn?.links || !Array.isArray(advertiseOn.links)) {
    return [];
  }

  const embeddedLinks: EmbeddedLink[] = [];

  // Extract all embeddable links with their metadata
  advertiseOn.links.forEach((linkGroup) => {
    const isResidenceSelectorGroup = linkGroup.text === "Boväljare";

    if (isResidenceSelectorGroup && linkGroup.links) {
      for (const url of linkGroup.links) {
        embeddedLinks.push({
          url,
          title: linkGroup.text || 'Länk',
          category: linkGroup.category || 'Info',
          embedUrl: getEmbedUrl(url, 'selector'),
          platform: 'selector',
        });
      }
    } else if (linkGroup.links && Array.isArray(linkGroup.links)) {
      linkGroup.links.forEach((url: string) => {
        const platform = getPlatform(url, linkGroup.text);
        if (platform) {
          embeddedLinks.push({
            url,
            title: linkGroup.text || 'Länk',
            category: linkGroup.category || 'Info',
            embedUrl: getEmbedUrl(url, platform),
            platform,
          });
        }
      });
    }
  });

  // Sort so videos (YouTube, Vimeo, Locka) appear before virtual tours and selector
  embeddedLinks.sort((a, b) => {
    const platformPriority = {
      youtube: 1,
      vimeo: 1,
      locka: 1,
      virtual_tour: 2,
      selector: 2,
    };

    const priorityA = platformPriority[a.platform] || 3;
    const priorityB = platformPriority[b.platform] || 3;

    return priorityA - priorityB;
  });

  return embeddedLinks;
};

/**
 * Filters embedded links to only video platforms (Vimeo and YouTube)
 * Optionally filters by category (default: 'Media')
 */
export const filterVideos = (
  links: EmbeddedLink[],
  category = 'Media'
): EmbeddedLink[] => {
  return links
    .filter((link) => link.platform === 'vimeo' || link.platform === 'youtube')
    .filter((link) => link.category === category);
};

/**
 * Extracts only video links from provider data
 * Convenience function that combines extractEmbeddedLinks and filterVideos
 */
export const extractVideoLinks = (
  entity: NestedHome | NestedProject,
  category = 'Media'
): EmbeddedLink[] => {
  return filterVideos(extractEmbeddedLinks(entity), category);
};

type EmbedPlatform = 'locka' | 'vimeo' | 'youtube' | 'virtual_tour' | 'selector';

/**
 * Determines the platform from a URL and optional text label
 */
const getPlatform = (url: string, text?: string): EmbedPlatform | null => {
  if (text === 'Virtuell visning') return 'virtual_tour';
  if (text === 'Boväljare') return 'selector';

  if (url.includes('locka.com')) return 'locka';
  if (url.includes('vimeo.com')) return 'vimeo';
  if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';

  return null;
};

/**
 * Gets the embed URL for a given link and platform
 */
const getEmbedUrl = (url: string, platform: EmbedPlatform): string => {
  switch (platform) {
    case 'locka':
      // Locka.com links are already embed URLs
      return url;

    case 'vimeo': {
      const videoId = extractVimeoVideoId(url);
      return videoId ? `https://player.vimeo.com/video/${videoId}` : url;
    }

    case 'youtube': {
      const videoId = extractYouTubeVideoId(url);
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    }

    case 'virtual_tour':
    case 'selector':
      // Virtual tour and Boväljare links are used as-is for iframe embedding
      return url;

    default:
      return url;
  }
};

/**
 * Extracts Vimeo video ID from various URL formats
 */
export const extractVimeoVideoId = (url: string): string | null => {
  // Handle player.vimeo.com/video/ID format
  const playerMatch = url.match(/player\.vimeo\.com\/video\/(\d+)/);
  if (playerMatch) {
    return playerMatch[1];
  }

  // Handle vimeo.com/ID format
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return vimeoMatch[1];
  }

  return null;
};

/**
 * Extracts YouTube video ID from various URL formats
 */
export const extractYouTubeVideoId = (url: string): string | null => {
  // Handle youtu.be/ID format
  const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch) {
    return shortMatch[1];
  }

  // Handle youtube.com/watch?v=ID format
  const watchMatch = url.match(/youtube\.com\/watch\?v=([^&]+)/);
  if (watchMatch) {
    return watchMatch[1];
  }

  // Handle youtube.com/embed/ID format
  const embedMatch = url.match(/youtube\.com\/embed\/([^?&]+)/);
  if (embedMatch) {
    return embedMatch[1];
  }

  return null;
};
